import { useEffect, useState } from 'react'
import { ScrollView, Text, View, useWindowDimensions } from 'react-native'
import { useTranslation } from 'react-i18next'
import { Gift, MoveRight, Timer } from 'lucide-react-native'
import { colors, getMemberFullName } from '@chillout/core'
import { AvatarInfo, BottomSheet, Button, DragHandle, InlineAlert } from '@chillout/ui'
import { ChangePinModal } from '../changePin/ChangePinModal'
import { RewardRedeemedView, type RedemptionResult } from './RewardRedeemedView'
import { useRedeemReward, InvalidPinError } from './useRedeemReward'
import type { CashierRedemptionScan } from '../home/useVerifyQr'

interface RewardFoundSheetProps {
  scan: CashierRedemptionScan | null
  onClose: () => void
  onScanNext?: () => void
}

function useQrCountdown(expiresAt: number): number {
  const [secondsLeft, setSecondsLeft] = useState(() =>
    Math.max(0, Math.floor((expiresAt - Date.now()) / 1000))
  )
  useEffect(() => {
    const tick = () => setSecondsLeft(Math.max(0, Math.floor((expiresAt - Date.now()) / 1000)))
    const id = setInterval(tick, 1000)
    return () => clearInterval(id)
  }, [expiresAt])
  return secondsLeft
}

function formatCountdown(seconds: number): string {
  const m = Math.floor(seconds / 60)
  const s = seconds % 60
  return `${m}:${s.toString().padStart(2, '0')}`
}

interface RewardFoundContentProps {
  scan: CashierRedemptionScan
  onContinue: () => void
}

function RewardFoundContent({ scan, onContinue }: RewardFoundContentProps) {
  const { t, i18n } = useTranslation()
  const secondsLeft = useQrCountdown(scan.expiresAt)
  const fullName = getMemberFullName(scan) ?? t('rewardRedemption.unknownMember')
  const lang = (i18n.language === 'sv' ? 'sv' : 'en') as 'en' | 'sv'
  const effectiveQuantity = Math.min(scan.quantity, scan.availableRewards)
  const hasInsufficientRewards = effectiveQuantity < scan.quantity
  const rewardLabel = effectiveQuantity > 1
    ? t('rewardRedemption.rewardWithQuantity', { name: scan.rewardItemName[lang], count: effectiveQuantity })
    : scan.rewardItemName[lang]
  const afterCount = scan.availableRewards - effectiveQuantity
  const isExpired = secondsLeft <= 0
  const validityColor = isExpired ? colors.accent.DEFAULT : colors.success

  return (
    <ScrollView contentContainerStyle={{ paddingBottom: 40 }}>
      {/* Member header */}
      <View className="px-6 pt-4 pb-4">
        <Text className="text-xl font-bold text-primary mb-4">
          {t('rewardRedemption.rewardFound')}
        </Text>
        <AvatarInfo
          name={fullName}
          email={scan.phone ?? scan.email ?? undefined}
          size={48}
        />
        <View className="border-t border-border mt-4" />
      </View>

      {/* Reward section */}
      <View className="px-6 gap-4">

        {/* Gift icon + name */}
        <View className="items-center gap-3 py-2">
          <View className="h-20 w-20 items-center justify-center rounded-2xl bg-canvas-primary">
            <Gift size={40} color={colors.accent.DEFAULT} strokeWidth={1.5} />
          </View>
          <Text className="text-2xl font-bold text-primary text-center">
            {rewardLabel}
          </Text>
        </View>

        {/* QR validity */}
        <View className="flex-row items-center justify-center gap-2">
          <Timer size={18} color={validityColor} strokeWidth={2.5} />
          <Text style={{ color: validityColor }} className="text-base font-semibold">
            {isExpired
              ? t('rewardRedemption.qrExpired')
              : t('rewardRedemption.qrValid', { countdown: formatCountdown(secondsLeft) })}
          </Text>
        </View>

        {/* Before → after — same style as StampPreview summary row */}
        <View className="rounded-lg bg-canvas-primary px-5 py-4">
          <View className="flex-row items-center justify-center">
            <View className="flex-row items-baseline gap-1">
              <Text className="text-lg font-bold text-primary">
                {t('rewardRedemption.currentCount')}:
              </Text>
              <Text className="text-lg font-bold text-accent">
                {scan.availableRewards}
              </Text>
            </View>
            <View className="px-3">
              <MoveRight size={18} color={colors.text.secondary} strokeWidth={2} />
            </View>
            <View className="flex-row items-baseline gap-1">
              <Text className="text-lg font-bold text-primary">
                {t('rewardRedemption.afterCount')}:
              </Text>
              <Text className="text-lg font-bold text-accent">
                {afterCount}
              </Text>
            </View>
          </View>
        </View>

        {hasInsufficientRewards && (
          <InlineAlert message={t('rewardRedemption.insufficientRewardsWarning', {
            available: scan.availableRewards,
            requested: scan.quantity,
          })} />
        )}

        <Button
          label={t('rewardRedemption.continueButton')}
          onPress={onContinue}
          disabled={isExpired || effectiveQuantity === 0}
        />
      </View>
    </ScrollView>
  )
}

export function RewardFoundSheet({ scan, onClose, onScanNext }: RewardFoundSheetProps) {
  const { t } = useTranslation()
  const { height } = useWindowDimensions()
  const { mutate: redeemReward, isPending: isRedeeming } = useRedeemReward()
  const [staffPinVisible, setStaffPinVisible] = useState(false)
  const [pinError, setPinError] = useState<string | null>(null)
  const [redemptionResult, setRedemptionResult] = useState<RedemptionResult | null>(null)

  useEffect(() => {
    if (scan) {
      setStaffPinVisible(false)
      setPinError(null)
      setRedemptionResult(null)
    }
  }, [scan])

  const handlePinConfirm = (pin: string) => {
    if (!scan) return
    const effectiveQuantity = Math.min(scan.quantity, scan.availableRewards)
    redeemReward(
      { redemptionTokenId: scan.redemptionTokenId, pin, quantity: effectiveQuantity },
      {
        onSuccess: (remainingCount) => {
          setStaffPinVisible(false)
          setRedemptionResult({
            rewardName:      scan.rewardItemName,
            quantity:        effectiveQuantity,
            memberFullName:  getMemberFullName(scan) ?? '',
            memberFirstName: scan.firstName ?? '',
            remainingCount,
          })
        },
        onError: (error) => {
          if (error instanceof InvalidPinError) {
            setPinError(t('rewardRedemption.staffPin.incorrectPin'))
          } else {
            setPinError(t('rewardRedemption.staffPin.redeemError'))
          }
        },
      },
    )
  }

  const handleDone = () => {
    setRedemptionResult(null)
    onClose()
  }

  const handleScanNext = () => {
    setRedemptionResult(null)
    if (onScanNext) onScanNext()
    else onClose()
  }

  return (
    <BottomSheet
      visible={scan !== null}
      onClose={onClose}
      backdropAccessibilityLabel={t('rewardRedemption.sheetBackdropAccessibilityLabel')}
    >
      <View
        style={{ maxHeight: height * 0.8 }}
        className="rounded-t-2xl bg-canvas-card overflow-hidden"
      >
        <DragHandle />
        {redemptionResult ? (
          <RewardRedeemedView
            result={redemptionResult}
            onScanNext={handleScanNext}
            onDone={handleDone}
          />
        ) : scan ? (
          <RewardFoundContent
            scan={scan}
            onContinue={() => {
              setPinError(null)
              setStaffPinVisible(true)
            }}
          />
        ) : null}
      </View>

      <ChangePinModal
        mode="entry"
        autoConfirm
        visible={staffPinVisible}
        isLoading={isRedeeming}
        title={t('rewardRedemption.staffPin.title')}
        subtitle={t('rewardRedemption.staffPin.subtitle')}
        modalAccessibilityLabel={t('rewardRedemption.staffPin.modalAccessibilityLabel')}
        closeAccessibilityLabel={t('rewardRedemption.staffPin.closeAccessibilityLabel')}
        errorMessage={pinError}
        getCodeAccessibilityLabel={(entered, total) =>
          t('rewardRedemption.staffPin.codeProgressAccessibilityLabel', { entered, total })}
        onInputChange={() => setPinError(null)}
        onClose={() => setStaffPinVisible(false)}
        onConfirm={handlePinConfirm}
      />
    </BottomSheet>
  )
}
